import { Shapes } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Capability, overviewTarget } from './capabilities'
import { CashForecast, DaysToPay, FuelSpend, ProfitPerTruck } from './CfoCharts'
import { usePinStore } from './pinnedArtifacts'

/** Artifacts this prototype can actually draw; the rest arrive as their frame. */
const DRAWN: Record<string, (() => React.ReactNode) | undefined> = {
  'Cash forecast': CashForecast,
  'Profit per truck': ProfitPerTruck,
}

/** The page artifacts get pinned to, empty until something is. */
export function CfoOverview({ teaser }: { teaser?: boolean }) {
  const pinned = usePinStore().pinnedFor(overviewTarget(Capability.CFO))

  return (
    <div className="no-scrollbar flex h-full flex-col gap-4 overflow-y-auto p-6">
      <h1 className="text-ink text-xl font-bold tracking-tight">CFO</h1>
      {pinned.length === 0 && teaser ? (
        // No dashed frame: this product is on, and the charts are its page
        // rather than a preview of one. Nothing real to plot until the
        // accounts are connected, so they stand faded behind the ask.
        <div className="flex min-h-0 flex-1 flex-col items-center gap-4">
          <div
            className="pointer-events-none grid min-h-0 w-full flex-1 grid-cols-2 grid-rows-2 gap-3 overflow-hidden opacity-40"
            aria-hidden
          >
            <CashForecast />
            <ProfitPerTruck />
            <FuelSpend />
            <DaysToPay />
          </div>
          <div className="flex max-w-md flex-col items-center gap-2 text-center">
            <Badge className="px-1.5 py-0 text-[10px]">Active</Badge>
            <p className="text-ink text-sm font-semibold">Look ahead and plan big money moves</p>
          </div>
          <div className="flex h-10 items-center">
            <Button size="sm" className="text-xs">
              Connect your accounts
            </Button>
          </div>
        </div>
      ) : pinned.length === 0 ? (
        <div className="border-ink-100 flex flex-1 flex-col items-center justify-center gap-3 rounded-md border border-dashed p-6">
          <Shapes className="text-ink-200 size-6" />
          <p className="text-ink text-sm font-semibold">Customize your overview</p>
          <p className="text-ink-500 max-w-sm text-center text-xs leading-relaxed">
            Create charts, spend trackers, revenue forecasts and other artifacts with Cai and pin them here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {pinned.map((title) => {
            const Drawn = DRAWN[title]
            return Drawn ? (
              <Drawn key={title} />
            ) : (
              <div key={title} className="border-ink-100 flex flex-col gap-2 rounded-sm border p-3">
                <span className="text-ink text-[11px] font-medium">{title}</span>
                <div className="bg-ink-50 h-20 rounded-sm" />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
